import { useState } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { Input } from "@/components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { useToast } from '@/hooks/use-toast';
import { supabase } from '@/lib/supabase';
import { useQueryClient } from '@tanstack/react-query';
import { Clock, Users, Calendar } from 'lucide-react';
import { format } from 'date-fns';

interface AddAvailabilityDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  hostId: string;
  selectedDate?: Date;
}

export const AddAvailabilityDialog = ({
  open,
  onOpenChange,
  hostId,
  selectedDate,
}: AddAvailabilityDialogProps) => {
  const [title, setTitle] = useState('');
  const [startTime, setStartTime] = useState('09:00');
  const [endTime, setEndTime] = useState('17:00');
  const [pattern, setPattern] = useState<'one_time' | 'weekly' | 'monthly'>('one_time');
  const [sessionType, setSessionType] = useState<'individual' | 'group'>();
  const [meetingType, setMeetingType] = useState<'online' | 'offline'>();
  const [maxParticipants, setMaxParticipants] = useState(1);
  const [pricePerPerson, setPricePerPerson] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [activeTab, setActiveTab] = useState('basic');
  
  const { toast } = useToast();
  const queryClient = useQueryClient();

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!selectedDate || !title) {
      toast({
        title: "Error",
        description: "Please fill in the title field.",
        variant: "destructive",
      });
      return;
    }

    if (!sessionType || !meetingType) {
      toast({
        title: "Error",
        description: "Please fill in all required fields in Session Details.",
        variant: "destructive",
      });
      setActiveTab('session');
      return;
    }

    if (sessionType === 'group' && (!maxParticipants || maxParticipants < 2)) {
      toast({
        title: "Error",
        description: "For group sessions, please specify a maximum number of participants (minimum 2).",
        variant: "destructive",
      });
      setActiveTab('session');
      return;
    }

    setIsSubmitting(true);
    try {
      const startDateTime = new Date(selectedDate);
      const [startHours, startMinutes] = startTime.split(':');
      startDateTime.setHours(parseInt(startHours), parseInt(startMinutes));

      const endDateTime = new Date(selectedDate);
      const [endHours, endMinutes] = endTime.split(':');
      endDateTime.setHours(parseInt(endHours), parseInt(endMinutes));

      const { error } = await supabase
        .from('host_availability')
        .insert({
          host_id: hostId,
          title,
          start_time: startDateTime.toISOString(),
          end_time: endDateTime.toISOString(),
          pattern,
          session_type: sessionType,
          meeting_type: meetingType,
          max_participants: sessionType === 'group' ? maxParticipants : 1,
          price_per_person: pricePerPerson ? parseFloat(pricePerPerson) : null,
        });

      if (error) throw error;

      toast({
        title: "Success",
        description: "Availability has been added.",
      });

      queryClient.invalidateQueries({ queryKey: ['host-availability'] });
      onOpenChange(false);
    } catch (error) {
      console.error('Error adding availability:', error);
      toast({
        title: "Error",
        description: "Failed to add availability. Please try again.",
        variant: "destructive",
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-[425px] max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>Add Availability</DialogTitle>
          <DialogDescription className="space-y-2">
            <div className="flex items-center gap-2 text-primary mt-2">
              <Calendar className="h-4 w-4" />
              <span className="font-medium">
                {selectedDate ? format(selectedDate, 'EEEE, MMMM d, yyyy') : 'Select a date'}
              </span>
            </div>
            <p className="text-muted-foreground">
              Set up a new time slot for hosting. You can choose between individual or group sessions.
            </p>
          </DialogDescription>
        </DialogHeader>
        <form onSubmit={handleSubmit}>
          <Tabs value={activeTab} onValueChange={setActiveTab} className="w-full">
            <TabsList className="grid w-full grid-cols-2">
              <TabsTrigger value="basic" className="flex items-center gap-2">
                <Clock className="h-4 w-4" />
                Basic Info
              </TabsTrigger>
              <TabsTrigger value="session" className="flex items-center gap-2">
                <Users className="h-4 w-4" />
                Session Details
              </TabsTrigger>
            </TabsList>

            <TabsContent value="basic" className="space-y-4 mt-4">
              <div className="space-y-2">
                <Label htmlFor="title" className="after:content-['*'] after:ml-0.5 after:text-red-500">Session Title</Label>
                <Input
                  id="title"
                  value={title}
                  onChange={(e) => setTitle(e.target.value)}
                  placeholder="e.g., Coffee Chat, City Tour, Language Exchange"
                  required
                />
                <p className="text-sm text-gray-500">Give your session a descriptive title</p>
              </div>
              <div className="space-y-2">
                <Label htmlFor="start-time">Start Time</Label>
                <Input
                  id="start-time"
                  type="time"
                  value={startTime}
                  onChange={(e) => setStartTime(e.target.value)}
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="end-time">End Time</Label>
                <Input
                  id="end-time"
                  type="time"
                  value={endTime}
                  onChange={(e) => setEndTime(e.target.value)}
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="pattern">Repeat Pattern</Label>
                <Select value={pattern} onValueChange={(value: 'one_time' | 'weekly' | 'monthly') => setPattern(value)}>
                  <SelectTrigger>
                    <SelectValue placeholder="Select pattern" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="one_time">One Time</SelectItem>
                    <SelectItem value="weekly">Weekly</SelectItem>
                    <SelectItem value="monthly">Monthly</SelectItem>
                  </SelectContent>
                </Select>
                <p className="text-sm text-gray-500">Choose if this slot should repeat weekly or monthly</p>
              </div>
            </TabsContent>

            <TabsContent value="session" className="space-y-4 mt-4">
              <div className="space-y-2">
                <Label htmlFor="meeting-type" className="after:content-['*'] after:ml-0.5 after:text-red-500">Meeting Type</Label>
                <Select value={meetingType} onValueChange={(value: 'online' | 'offline') => setMeetingType(value)} required>
                  <SelectTrigger>
                    <SelectValue placeholder="Select meeting type" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="offline">In Person</SelectItem>
                    <SelectItem value="online">Online</SelectItem>
                  </SelectContent>
                </Select>
                <p className="text-sm text-gray-500">Choose whether this will be an online or in-person session</p>
              </div>
              <div className="space-y-2">
                <Label htmlFor="session-type" className="after:content-['*'] after:ml-0.5 after:text-red-500">Session Type</Label>
                <Select value={sessionType} onValueChange={(value: 'individual' | 'group') => setSessionType(value)} required>
                  <SelectTrigger>
                    <SelectValue placeholder="Select session type" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="individual">Individual</SelectItem>
                    <SelectItem value="group">Group</SelectItem>
                  </SelectContent>
                </Select>
                <p className="text-sm text-gray-500">Individual sessions are one-on-one, group sessions allow multiple participants</p>
              </div>
              {sessionType === 'group' && (
                <div className="space-y-2">
                  <Label htmlFor="max-participants" className="after:content-['*'] after:ml-0.5 after:text-red-500">Maximum Participants</Label>
                  <Input
                    id="max-participants"
                    type="number"
                    min={2}
                    value={maxParticipants}
                    onChange={(e) => setMaxParticipants(parseInt(e.target.value))}
                    required
                  />
                  <p className="text-sm text-gray-500">Set a limit for the number of participants</p>
                </div>
              )}
              <div className="space-y-2">
                <Label htmlFor="price">Price per Person ($)</Label>
                <Input
                  id="price"
                  type="number"
                  step="0.01"
                  min="0"
                  value={pricePerPerson}
                  onChange={(e) => setPricePerPerson(e.target.value)}
                  placeholder="Enter price per person"
                />
                <p className="text-sm text-gray-500">Set your rate per person for this session</p>
              </div>
            </TabsContent>
          </Tabs>

          <DialogFooter className="mt-6">
            <Button type="submit" disabled={isSubmitting}>
              {isSubmitting ? "Adding..." : "Add Availability"}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
};